import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { UploadSimple, FilePdf, X, CheckCircle, Warning, ArrowRight } from "@phosphor-icons/react";
import UploadCountdown from "@/components/UploadCountdown";

export default function Upload() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [pendingMeta, setPendingMeta] = useState(null); // {count, size}
  const [result, setResult] = useState(null);
  // Lista de duplicados detectados durante la subida. Cada item:
  //   { expediente_id, numero_siniestro, aseguradora_name, uploaded_filename }
  const [duplicates, setDuplicates] = useState([]);
  const [drag, setDrag] = useState(false);

  const onPick = (e) => {
    const list = Array.from(e.target.files || []);
    addFiles(list);
  };

  const addFiles = (list) => {
    const pdfs = list.filter((f) => f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf"));
    setFiles((prev) => [...prev, ...pdfs]);
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    addFiles(Array.from(e.dataTransfer.files || []));
  };

  const remove = (idx) => setFiles(files.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!files.length) return;
    const meta = {
      count: files.length,
      size: files.reduce((a, f) => a + f.size, 0),
    };
    setPendingMeta(meta);
    setUploading(true);
    setResult(null);
    setDuplicates([]);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      const r = await api.post("/expedientes/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const dups = r.data.duplicates || [];
      setDuplicates(dups);
      setResult({ ok: true, count: r.data.created, expedientes: r.data.expedientes, hasDuplicates: dups.length > 0 });
      // Mantenemos en la lista SOLO los archivos que NO se han creado y no son duplicados,
      // para que el usuario pueda intentar subirlos de nuevo. Si todos OK, vaciamos.
      const dupNames = new Set(dups.map((d) => d.uploaded_filename));
      setFiles((prev) => prev.filter((f) => dupNames.has(f.name)));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      // El backend devuelve 409 con detail.code === "duplicate_siniestro" cuando
      // TODOS los partes subidos son duplicados.
      if (detail && typeof detail === "object" && detail.code === "duplicate_siniestro") {
        setDuplicates(detail.duplicates || []);
        setResult({ ok: false, error: detail.message || "Siniestros duplicados", isDuplicate: true });
      } else {
        setResult({ ok: false, error: typeof detail === "string" ? detail : "Error al subir" });
      }
    } finally {
      setUploading(false);
      setPendingMeta(null);
    }
  };

  return (
    <div data-testid="upload-page">
      {uploading && pendingMeta && (
        <UploadCountdown
          fileCount={pendingMeta.count}
          totalSizeBytes={pendingMeta.size}
          onCancel={() => navigate("/inbox")}
        />
      )}
      <header className="h-14 border-b border-border bg-white px-6 flex items-center sticky top-0 z-20">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-muted">Añadir</div>
          <h1 className="font-display font-bold text-lg tracking-tight">Nuevos siniestros</h1>
        </div>
      </header>

      <div className="p-6 max-w-3xl">
        <p className="text-sm text-muted mb-4">
          Sube PDFs de partes de siniestro de las aseguradoras. Cada PDF se convierte en un expediente y la IA extrae los datos automáticamente.
        </p>

        {/* Drop zone */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={onDrop}
          onClick={() => inputRef.current?.click()}
          data-testid="dropzone"
          className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-all ${
            drag ? "border-foreground bg-surface-2" : "border-border bg-surface hover:bg-surface-2"
          }`}
        >
          <UploadSimple className={`w-10 h-10 mx-auto mb-3 ${drag ? "text-foreground" : "text-muted"}`} weight="bold" />
          <div className="font-display font-bold text-lg mb-1 tracking-tight">
            Arrastra PDFs aquí o haz clic
          </div>
          <div className="text-xs text-muted">
            Hasta 50 archivos PDF a la vez · Aseguradoras: Ocaso, Occident, Mapfre, Línea Directa, Mutua, Aegon...
          </div>
          <input
            ref={inputRef}
            type="file"
            multiple
            accept="application/pdf,.pdf"
            className="hidden"
            onChange={onPick}
            data-testid="upload-file-input"
          />
        </div>

        {/* Files list */}
        {files.length > 0 && (
          <div className="mt-6 border border-border rounded-md bg-white">
            <div className="px-4 py-3 border-b border-border flex items-center justify-between">
              <div className="text-sm font-semibold">{files.length} archivo{files.length > 1 ? "s" : ""}</div>
              <button onClick={() => setFiles([])} className="text-xs text-muted hover:text-foreground" data-testid="clear-files">
                Quitar todos
              </button>
            </div>
            <ul className="divide-y divide-border">
              {files.map((f, i) => (
                <li key={`${f.name}-${f.size}-${i}`} className="flex items-center gap-3 px-4 py-2.5 text-sm">
                  <FilePdf className="w-5 h-5 text-status-error-fg" weight="duotone" />
                  <div className="flex-1 min-w-0">
                    <div className="font-mono text-[12px] truncate">{f.name}</div>
                    <div className="text-[11px] text-muted">{(f.size / 1024).toFixed(0)} KB</div>
                  </div>
                  <button onClick={() => remove(i)} className="p-1 hover:bg-surface rounded text-muted" data-testid={`remove-file-${i}`}>
                    <X className="w-4 h-4" weight="bold" />
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Submit */}
        <div className="mt-6 flex items-center justify-between">
          <div className="text-xs text-muted">
            {files.length > 0 && `Listos: ${files.reduce((a, f) => a + f.size, 0) / 1024 / 1024 | 0} MB`}
          </div>
          <button
            onClick={submit}
            disabled={!files.length || uploading}
            data-testid="upload-submit"
            className="bg-foreground text-white px-5 py-2.5 rounded-md text-sm font-medium hover:bg-foreground/90 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {uploading ? "Procesando..." : `Añadir ${files.length || ""} siniestro${files.length === 1 ? "" : "s"}`.trim()}
          </button>
        </div>

        {/* Result */}
        {result?.ok && result.count > 0 && (
          <div className="mt-6 p-5 rounded-md bg-status-success-bg border border-status-success-bd text-status-success-fg" data-testid="upload-success">
            <div className="flex items-center gap-2 mb-2">
              <CheckCircle className="w-5 h-5" weight="fill" />
              <div className="font-semibold">{result.count} expediente{result.count !== 1 ? "s" : ""} en cola</div>
            </div>
            <p className="text-sm">La IA está extrayendo los datos. En unos segundos podrás revisarlos.</p>
            <div className="mt-3 flex gap-2">
              <button
                onClick={() => navigate("/inbox")}
                className="bg-status-success-fg text-white text-xs px-3 py-1.5 rounded-md font-medium"
                data-testid="goto-inbox"
              >Ver bandeja</button>
              <button onClick={() => { setResult(null); setDuplicates([]); }} className="text-xs px-3 py-1.5">Subir más</button>
            </div>
          </div>
        )}
        {result && !result.ok && !result.isDuplicate && (
          <div className="mt-6 p-4 rounded-md bg-status-error-bg border border-status-error-bd text-status-error-fg flex items-center gap-2" data-testid="upload-error">
            <Warning className="w-5 h-5" weight="fill" />
            <div className="text-sm">{result.error}</div>
          </div>
        )}
      </div>

      {/* Popup de duplicados (modal) */}
      {duplicates.length > 0 && (
        <DuplicateModal
          duplicates={duplicates}
          onClose={() => { setDuplicates([]); if (result?.isDuplicate) setResult(null); }}
          onOpen={(expId) => navigate(`/review/${expId}`)}
        />
      )}
    </div>
  );
}

function DuplicateModal({ duplicates, onClose, onOpen }) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center p-4" data-testid="duplicate-modal">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white rounded-lg shadow-xl border border-border overflow-hidden">
        <div className="px-5 py-4 bg-status-warning-bg border-b border-status-warning-bd flex items-start gap-3">
          <Warning className="w-6 h-6 text-status-warning-fg shrink-0 mt-0.5" weight="fill" />
          <div className="flex-1">
            <h3 className="font-display font-bold text-base tracking-tight">
              {duplicates.length === 1 ? "Siniestro ya existente" : `${duplicates.length} siniestros ya existentes`}
            </h3>
            <p className="text-xs text-muted mt-0.5">
              No se ha{duplicates.length === 1 ? "" : "n"} subido. Abre el expediente existente para añadir documentación si lo necesitas.
            </p>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/40 rounded" data-testid="duplicate-modal-close" aria-label="Cerrar">
            <X className="w-5 h-5" weight="bold" />
          </button>
        </div>
        <ul className="divide-y divide-border max-h-[60vh] overflow-y-auto">
          {duplicates.map((d) => (
            <li key={d.expediente_id} className="px-5 py-3 flex items-center gap-3" data-testid={`duplicate-item-${d.expediente_id}`}>
              <FilePdf className="w-6 h-6 text-status-error-fg shrink-0" weight="duotone" />
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">Nº {d.numero_siniestro || "—"}</div>
                <div className="text-[11px] text-muted truncate">
                  {d.aseguradora_name || "Aseguradora no identificada"}
                  {d.uploaded_filename && <> · archivo: <span className="font-mono">{d.uploaded_filename}</span></>}
                </div>
              </div>
              <button
                onClick={() => onOpen(d.expediente_id)}
                className="text-xs font-semibold inline-flex items-center gap-1 px-3 py-2 rounded-md bg-foreground text-white hover:bg-foreground/90"
                data-testid={`duplicate-open-${d.expediente_id}`}
              >
                Abrir <ArrowRight className="w-3 h-3" weight="bold" />
              </button>
            </li>
          ))}
        </ul>
        <div className="px-5 py-3 border-t border-border flex justify-end">
          <button
            onClick={onClose}
            className="text-xs px-3 py-2 rounded-md bg-surface border border-border hover:bg-surface-2"
            data-testid="duplicate-modal-dismiss"
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
}
